import React from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Navigate, useLocation } from 'react-router-dom'
import { roleOf, type AppRole } from '@/lib/roles'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: AppRole
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { user, loading } = useAuth()
  const location = useLocation()

  // show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-400">Verifying authentication...</p>
        </div>
      </div>
    )
  }

  // redirect to login if not authenticated
  if (!user) {
    // token in localStorage means AuthContext is still catching up, show spinner
    if (localStorage.getItem('access_token')) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-900">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )
    }
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // check role requirements if specified
  if (requiredRole && roleOf(user) !== requiredRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-slate-400 mb-4">
            You don't have permission to access this resource.
          </p>
          <button
            onClick={() => window.history.back()}
            className="text-primary hover:text-primary/80"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}